/**
 * Detection rules.
 *
 * The column that earns its place is "unknown fields". A rule referencing a field no configured
 * source emits looks correct, validates, and silently matches nothing forever — the single most
 * common reason a new detection never fires. Surfacing it in the list is worth more than any
 * amount of rule-authoring documentation.
 */

import { useMemo, useState } from 'react'
import { api, type Backtest, type Rule, type SigmaImport } from '../api'
import { useSession } from '../App'
import {
  Async,
  Card,
  Chip,
  Empty,
  Modal,
  Mono,
  Note,
  SeverityChip,
  Stat,
  fmtDuration,
  useAsync,
  useToast,
} from '../components/ui'

/**
 * Import a Sigma rule.
 *
 * Look, then decide: the first request converts and reports, and only a second one saves. An
 * import that saved immediately would put detection content into the estate before anybody had
 * read what it refused to translate — and Sigma expresses things this platform's condition algebra
 * does not, so those refusals are the interesting part.
 */
function ImportSigma({ onImported }: { onImported: () => void }) {
  const { can } = useSession()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [doc, setDoc] = useState('')
  const [preview, setPreview] = useState<SigmaImport | null>(null)
  const [refusals, setRefusals] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  if (!can('configure_rules')) return null

  function send(save: boolean) {
    setBusy(true)
    setRefusals([])
    api
      .post<SigmaImport>('/rules/import/sigma', { document: doc, save })
      .then((r) => {
        setPreview(r)
        if (r.saved) {
          toast('ok', `Imported "${r.rule.name}", disabled.`)
          setOpen(false)
          setDoc('')
          setPreview(null)
          onImported()
        }
      })
      // Every reason at once, not the first: fixing them one run at a time is how an import gets
      // abandoned.
      .catch((e: Error) => setRefusals(e.message.split('\n')))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <button className="sm" onClick={() => setOpen(true)}>
        Import Sigma
      </button>
      {open && (
        <Modal
          title="Import a Sigma rule"
          onClose={() => setOpen(false)}
          wide
          footer={
            <>
              <button onClick={() => setOpen(false)}>Cancel</button>
              <button disabled={busy || !doc.trim()} onClick={() => send(false)}>
                {busy ? 'Converting…' : 'Convert'}
              </button>
              <button
                className="primary"
                disabled={busy || !preview}
                onClick={() => send(true)}
                title={preview ? 'Save it, disabled' : 'Convert it first'}
              >
                Save, disabled
              </button>
            </>
          }
        >
          <div className="field">
            <label htmlFor="sigma-doc">The rule, as YAML</label>
            <textarea
              id="sigma-doc"
              rows={12}
              className="mono"
              value={doc}
              onChange={(e) => {
                setDoc(e.target.value)
                setPreview(null)
              }}
              placeholder={'title: ...\ndetection:\n    selection:\n        Image|endswith: ...\n    condition: selection'}
            />
          </div>

          {refusals.length > 0 && (
            <Note tone="danger">
              <strong>Not imported.</strong> Sigma expresses things this platform's condition
              algebra does not, and translating them to the nearest thing would produce a rule that
              matches something other than what its author wrote:
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {refusals.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </Note>
          )}

          {preview && (
            <>
              <Card title={preview.rule.name} subtitle="What it would become">
                <dl className="kv">
                  <dt>Id</dt>
                  <dd><Mono>{preview.rule.id}</Mono></dd>
                  <dt>Severity</dt>
                  <dd><SeverityChip severity={preview.rule.severity} /></dd>
                  <dt>Techniques</dt>
                  <dd>
                    {preview.rule.techniques.length === 0
                      ? '—'
                      : preview.rule.techniques.map((t) => (
                          <Chip key={t.id} tone="outline">
                            {t.id}
                          </Chip>
                        ))}
                  </dd>
                  <dt>State</dt>
                  <dd><Chip tone="neutral">disabled on import</Chip></dd>
                </dl>
                <pre
                  className="mono"
                  style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}
                >
                  {JSON.stringify(preview.rule.condition, null, 2)}
                </pre>
              </Card>

              {preview.unknown_fields.length > 0 && (
                <Note tone="warn">
                  This rule reads <Mono>{preview.unknown_fields.join(', ')}</Mono>, which no
                  configured log source produces. Sigma names fields as the source emits them and
                  this platform names them as its field policy normalized them — so either map
                  those fields at the source, or change the rule to use fields you have. Until then
                  it will match nothing, and testing it will say so.
                </Note>
              )}
              {preview.warnings.map((w, i) => (
                <Note key={i} tone="warn">
                  {w}
                </Note>
              ))}
            </>
          )}
        </Modal>
      )}
    </>
  )
}

export default function Rules() {
  const { can, me } = useSession()
  const toast = useToast()
  const rules = useAsync<Rule[]>(() => api.get<Rule[]>('/rules'), [me.tenant])
  const [q, setQ] = useState('')
  const [tactic, setTactic] = useState('')
  const [onlyProblems, setOnlyProblems] = useState(false)
  const [inspecting, setInspecting] = useState<Rule | null>(null)

  const tactics = useMemo(() => {
    const s = new Set<string>()
    for (const r of rules.data ?? []) for (const t of r.techniques) s.add(t.tactic)
    return [...s].sort()
  }, [rules.data])

  function toggle(r: Rule) {
    api
      .put('/rules', { ...r, enabled: !r.enabled })
      .then(() => {
        toast('ok', `${r.name} ${r.enabled ? 'disabled' : 'enabled'}`)
        rules.reload()
      })
      .catch((e: Error) => toast('error', e.message))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Detection rules</h1>
          <div className="sub">
            Rules produce signals, not alerts. Being generous here is fine — correlation decides
            which chains of signals become incidents, so recall lives at this layer and precision
            lives at the next one.
          </div>
        </div>
        <div className="actions">
          <ImportSigma onImported={rules.reload} />
        </div>
      </div>

      <Card
        title="Rules"
        actions={
          <>
            <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} aria-label="Search rules" />
            <select value={tactic} onChange={(e) => setTactic(e.target.value)} style={{ width: 'auto' }} aria-label="Tactic">
              <option value="">All tactics</option>
              {tactics.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <label className="row small muted" style={{ margin: 0, gap: 5 }}>
              <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
              Only ones with problems
            </label>
          </>
        }
        tight
      >
        <Async state={rules} empty={<Empty title="No rules" />}>
          {(list) => {
            const rows = list
              .filter((r) => !q || `${r.name} ${r.id} ${r.description}`.toLowerCase().includes(q.toLowerCase()))
              .filter((r) => !tactic || r.techniques.some((t) => t.tactic === tactic))
              .filter((r) => !onlyProblems || r.warnings.length > 0 || r.unknown_fields.length > 0)

            const broken = list.filter((r) => r.unknown_fields.length > 0)
            return (
              <>
                {broken.length > 0 && !onlyProblems && (
                  <div style={{ padding: 12 }}>
                    <Note tone="warn">
                      <strong>{broken.length} rule(s) reference fields no configured source emits.</strong>{' '}
                      They will never fire.{' '}
                      <button className="sm ghost" onClick={() => setOnlyProblems(true)}>
                        Show them
                      </button>
                    </Note>
                  </div>
                )}
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Severity</th>
                        <th>Rule</th>
                        <th>Trigger</th>
                        <th>ATT&amp;CK</th>
                        <th>Playbook</th>
                        <th>State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className="clickable" onClick={() => setInspecting(r)}>
                          <td><SeverityChip severity={r.severity} /></td>
                          <td>
                            <div style={{ fontWeight: 550 }}>{r.name}</div>
                            <div className="faint small mono">{r.id}</div>
                            {r.unknown_fields.length > 0 && (
                              <Chip tone="danger" title={`Unknown: ${r.unknown_fields.join(', ')}`}>
                                will never fire
                              </Chip>
                            )}
                            {r.warnings.length > 0 && <Chip tone="warn">{r.warnings.length} warning(s)</Chip>}
                          </td>
                          <td className="small muted">
                            {typeof r.trigger.kind === 'string' && r.trigger.kind === 'threshold' ? (
                              <>
                                {String(r.trigger.count)} in{' '}
                                {fmtDuration(Number(r.trigger.window_ms))}
                                {r.trigger.distinct_by ? <div className="faint">distinct</div> : null}
                              </>
                            ) : (
                              'per event'
                            )}
                          </td>
                          <td className="small">
                            {r.techniques.slice(0, 3).map((t) => (
                              <Chip key={t.id} tone="outline" title={`${t.name} · ${t.tactic}`}>
                                {t.id}
                              </Chip>
                            ))}
                          </td>
                          <td className="small mono truncate" style={{ maxWidth: '20ch' }}>
                            {r.playbook_id ?? '—'}
                          </td>
                          <td>
                            {can('configure_rules') ? (
                              <button
                                className="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggle(r)
                                }}
                              >
                                {r.enabled ? 'Disable' : 'Enable'}
                              </button>
                            ) : (
                              <Chip tone={r.enabled ? 'ok' : 'neutral'}>{r.enabled ? 'enabled' : 'disabled'}</Chip>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          }}
        </Async>
      </Card>

      {inspecting && <RuleDetail rule={inspecting} onClose={() => setInspecting(null)} />}
    </>
  )
}

/**
 * What this rule would have done against the retained window.
 *
 * Shown where the author is, before enabling anything. The result is always an undercount —
 * retention drops events and sampling means some were never stored — so the notes are rendered as
 * prominently as the number rather than tucked away.
 */
function RuleTest({ rule }: { rule: Rule }) {
  const { can } = useSession()
  const toast = useToast()
  const [result, setResult] = useState<Backtest | null>(null)
  const [busy, setBusy] = useState(false)
  if (!can('configure_rules')) return null

  return (
    <Card
      title="What it would have done"
      subtitle="Replayed against the events still retained"
      actions={
        <button
          className="sm"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            api
              .post<Backtest>('/rules/test', rule)
              .then(setResult)
              .catch((e: Error) => toast('error', e.message))
              .finally(() => setBusy(false))
          }}
        >
          {busy ? 'Replaying…' : result ? 'Run again' : 'Test this rule'}
        </button>
      }
    >
      {!result ? (
        <p className="small muted" style={{ margin: 0 }}>
          Replays this rule through the production detection engine over the retained window. It
          changes nothing and saves nothing.
        </p>
      ) : (
        <>
          <div className="grid cols-3" style={{ marginBottom: 10 }}>
            <Stat label="Signals" value={String(result.signals)} />
            <Stat label="Events matched" value={String(result.matched)} />
            <Stat label="Events replayed" value={String(result.events_considered)} />
          </div>

          {result.by_day.length > 0 && (
            <div className="row wrap" style={{ gap: 6, marginBottom: 10 }}>
              {result.by_day.map(([day, n]) => (
                <Chip key={day} tone={n >= 20 ? 'warn' : 'outline'}>
                  {day}: {n}
                </Chip>
              ))}
            </div>
          )}

          {result.samples.length > 0 && (
            <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
              {result.samples.slice(0, 5).map((s) => (
                <li key={s.id} className="small">
                  <SeverityChip severity={s.severity} /> {s.title}
                </li>
              ))}
            </ul>
          )}

          {/* Two kinds of note: what is wrong with the rule, and why the number above is lower
              than the truth. The first is the author's to fix; the second never goes away. */}
          {result.notes.map((n, i) => (
            <Note key={i} tone={n.startsWith('The rule itself:') ? 'danger' : 'warn'}>
              {n}
            </Note>
          ))}
          {result.signals === 0 && result.matched === 0 && (
            <Note>
              Nothing matched in the retained window. That is not proof the rule is wrong — the
              activity may simply not be in it.
            </Note>
          )}
        </>
      )}
    </Card>
  )
}

function RuleDetail({ rule, onClose }: { rule: Rule; onClose: () => void }) {
  return (
    <Modal title={rule.name} onClose={onClose} wide footer={<button onClick={onClose}>Close</button>}>
      <div className="stack">
        {rule.unknown_fields.length > 0 && (
          <Note tone="danger">
            This rule reads <Mono>{rule.unknown_fields.join(', ')}</Mono>, which no configured log
            source produces. It will match nothing until a source supplying those fields is
            onboarded, or the rule is changed to use fields you have.
          </Note>
        )}
        {rule.warnings.map((w, i) => (
          <Note key={i} tone="warn">
            {w}
          </Note>
        ))}

        {rule.description && <p>{rule.description}</p>}

        {rule.triage_guidance && (
          <Card title="Triage guidance" subtitle="Shown to whoever picks this up">
            <div style={{ whiteSpace: 'pre-wrap' }}>{rule.triage_guidance}</div>
          </Card>
        )}

        <dl className="kv">
          <dt>Id</dt>
          <dd><Mono copy>{rule.id}</Mono></dd>
          <dt>Version</dt>
          <dd>{rule.version}</dd>
          <dt>Source</dt>
          <dd>{rule.source.replace(/_/g, ' ')}{rule.author && ` · ${rule.author}`}</dd>
          <dt>Severity</dt>
          <dd><SeverityChip severity={rule.severity} /></dd>
          <dt>Throttle</dt>
          <dd>{rule.throttle.window_ms ? fmtDuration(rule.throttle.window_ms) : 'none — this can flood'}</dd>
          <dt>Evidence cap</dt>
          <dd>{rule.evidence_cap} events cited per signal</dd>
          <dt>Techniques</dt>
          <dd>
            {rule.techniques.map((t) => (
              <div key={t.id}>
                <Chip tone="outline">{t.id}</Chip> {t.name} <span className="faint">· {t.tactic}</span>
              </div>
            ))}
          </dd>
          <dt>Requirements served</dt>
          <dd>{rule.pir_ids.join(', ') || '—'}</dd>
        </dl>

        <RuleTest rule={rule} />

        <Card title="Condition" subtitle="The exact logic, as evaluated">
          <pre
            className="mono"
            style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}
          >
            {JSON.stringify(rule.condition, null, 2)}
          </pre>
        </Card>
      </div>
    </Modal>
  )
}
